import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import { identity } from "lodash";
import { PopupClickContainer } from "@similarweb/ui-components";
import { ChipWrapper } from "../styledComponents";
import { SimpleChipItemWrap } from "@similarweb/ui-components";
import { AdvancedFilterButton } from "pro-features/components/filtersPanel/src/filtersPanel";
import { MultiIncludeExcludePopupContent } from "./MultiIncludeExcludePopupContent";
import { GlobalStyles } from "./styledComponents";
import type { IRule, IRuleItem, MultiIncludeExcludeWebsitesProps } from "./types";
import { createNewRow, MultiIncludeExcludeWebsitesTextField } from "./utils";
import { allTrackers } from "app/services/track/track";

export const MultiIncludeExcludeWebsites: FC<MultiIncludeExcludeWebsitesProps> = ({
    rules: initialRules,
    onMultiIncludeExcludeChange,
    getButtonText,
    maxRules = 3,
    maxWebsitesPerRule = 20,
    withMatchType = true,
    getRuleSeparatorText,
}) => {
    const filterPopupRef = useRef(null);
    const contentContainerRef = useRef(null);
    const didApply = useRef(false);
    const [rules, setRules] = useState<IRule[]>(initialRules ?? [createNewRow()]);

    useEffect(() => {
        if (!rules.length) {
            setRules([createNewRow()]);
        }
    }, [rules]);

    const onChipdownToggle = (isOpen: boolean) => {
        if (isOpen) {
            didApply.current = false;
            allTrackers.dispatchTrackEvent("Drop Down", "click", "Open website filter");
        } else if (!didApply.current) {
            onApply(false);
        }
    };

    const onCancelClick = (e: Event) => {
        e.stopPropagation();
        setRules(initialRules ?? [createNewRow()]);
        filterPopupRef.current.closePopup();
    };

    const addRule = () => {
        allTrackers.dispatchTrackEvent("Drop Down", "click", "Add rule");
        setRules([...rules, createNewRow()]);
        queueMicrotask(() => {
            contentContainerRef.current.scrollTop = contentContainerRef.current.scrollHeight;
        });
    };

    const deleteRule = (index: number) => {
        setRules([...rules.slice(0, index), ...rules.slice(index + 1)]);
    };

    const onRowTypeChange = (index: number, rule: Partial<Pick<IRule, "matchType" | "type">>) => {
        setRules([...rules.slice(0, index), createNewRow({ ...rules[index], ...rule }), ...rules.slice(index + 1)]);
    };

    const onWebsiteSelected = (website: string, index: number) => {
        // lowercase the string
        website = website?.toLowerCase?.();
        // remove http and www from the beginning of the string
        website = website?.replace?.(/^(https?:\/\/)?(www\.)?/i, "");
        if (!website) {
            return;
        }
        const trackWebsiteAdd = (website: string) => {
            allTrackers.dispatchTrackEvent("Drop Down", "click", `keyword added/${website || ""}`);
        };
        const commaSeparatedItems = website
            .split(",")
            .map((item) => item.trim())
            .filter(identity);

        trackWebsiteAdd(commaSeparatedItems.join(","));

        const removeDuplicates = (items: IRuleItem[]) => {
            return items.filter(
                (item, index, self) => index === self.findIndex((t) => t.type === item.type && t.value === item.value),
            );
        };

        if (commaSeparatedItems.length > 1) {
            setRules((currentRules) => {
                currentRules[index].items.push(
                    ...commaSeparatedItems.map((value) => ({ type: "website", value }) as const),
                );
                currentRules[index].items = removeDuplicates(currentRules[index].items);
                currentRules[index].items = currentRules[index].items.slice(0, maxWebsitesPerRule);
                return [...currentRules];
            });
        } else if (website.trim().length) {
            setRules((currentRules) => {
                currentRules[index].items.push({ type: "website", value: website });
                currentRules[index].items = removeDuplicates(currentRules[index].items);
                return [...currentRules];
            });
        }
    };

    const onRemoveWebsite = (website: string, type: "website", _, index: number) => {
        setRules((currentRules) => {
            currentRules[index].items = currentRules[index].items.filter(
                (item) => item.type === type && item.value !== website,
            );
            return [...currentRules];
        });
    };

    const onApply = (closePopup = true) => {
        didApply.current = true;
        // remove empty rules
        const rulesToSubmit = rules.filter((rule) => rule.items.length);
        onMultiIncludeExcludeChange(rulesToSubmit);
        if (closePopup) {
            filterPopupRef.current.closePopup();
        }

        /**
         * Track event
         * @returns {{websitesCount: number, ruleCount: number, ruleTypes: string}}} - where ruleTypes is a comma separated string of rule types+match types
         */
        const { websiteCount, ruleCount, ruleTypes } = rulesToSubmit.reduce(
            (acc, rule) => {
                acc.ruleCount++;
                acc.ruleTypes += `, ${rule.type}+${rule.matchType}`;
                acc.websiteCount += rule.items.filter((item) => item.type === "website").length;
                return acc;
            },
            { websiteCount: 0, ruleCount: 0, ruleTypes: "" },
        );

        allTrackers.dispatchTrackEvent(
            "Drop Down",
            "click",
            `website filter applied/${websiteCount.toString() || ""}/${ruleCount.toString() || ""} rules${ruleTypes || ""}`,
        );
    };

    const onResetClick = (e) => {
        e.stopPropagation();
        onMultiIncludeExcludeChange([]);
    };

    const popupConfig = {
        width: 750,
        enabled: true,
        placement: "ontop-left",
        cssClassContent: "multiIncludeExclude--Popup-container",
        cssClassContainer: "Popup-element-wrapper-triangle multiIncludeExclude--Popup-container",
        onToggle: onChipdownToggle,
    };

    return (
        <>
            <GlobalStyles />
            <PopupClickContainer
                data-automation="website-multi-include-exclude-websites-chipdown"
                config={popupConfig}
                ref={filterPopupRef}
                content={() => (
                    <MultiIncludeExcludePopupContent
                        fieldType="websites"
                        rules={rules}
                        maxItemsPerRule={maxWebsitesPerRule}
                        addRow={addRule}
                        deleteRow={deleteRule}
                        onRowTypeChange={onRowTypeChange}
                        onItemSelected={onWebsiteSelected}
                        onRemoveItem={onRemoveWebsite}
                        onApply={onApply}
                        onCancelClick={onCancelClick}
                        maxRules={maxRules}
                        withMatchType={withMatchType}
                        setContainerRef={(ref) => (contentContainerRef.current = ref)}
                        getRuleSeparatorText={getRuleSeparatorText}
                    >
                        {MultiIncludeExcludeWebsitesTextField}
                    </MultiIncludeExcludePopupContent>
                )}
            >
                <div>
                    {rules[0]?.items?.length ? (
                        <ChipWrapper>
                            <SimpleChipItemWrap
                                text={getButtonText(rules)}
                                onCloseItem={onResetClick}
                            />
                        </ChipWrapper>
                    ) : (
                        <AdvancedFilterButton>{getButtonText()}</AdvancedFilterButton>
                    )}
                </div>
            </PopupClickContainer>
        </>
    );
};
